/**
 * ChannelShareBarChart · Module B - Active Ads Channel Share 横向堆叠柱状图
 *
 * 需求：
 * - 横向柱状图（Y 轴=游戏，X 轴=指标值），各渠道占比按游戏堆叠
 * - 图例 = 渠道
 * - tooltip 展示游戏名称 + 各渠道数值及占比
 * - 游戏数量 > dataZoomThreshold 时启用纵向滚动
 *
 * 仅在 metric === 'creative_count_stat_by_channel_percent' 时使用
 */
import React, { memo, useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { formatChartParamsDate, channelShowElement } from '@op/components/ChartCommon/formatter';
import { DBO_EC, DBO_V } from '@op/utils/format';
import {
  ChannelIconType,
  channelInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import { IChannelChartBarItem } from '../../types/channel';
import { TMetric } from '../../types/common';
import { TGameMap } from '../../hooks/useGameMap';
import { TChannelMap } from '../../hooks/useChannelMap';
import { METRIC_METAS } from '../../constants/metrics';
import {
  buildGameRich,
  gameRichKey,
  gameTooltipLabel,
} from '../TrendCompare/utils';
import type { IGameSortValue } from './GameSortSelect';
import { ClassSpecial } from '@op/constants/common';

export interface IChannelShareBarChartProps {
  /** 各渠道的游戏指标 */
  list: IChannelChartBarItem[];
  gameMap: TGameMap;
  /** 渠道元信息映射（id → meta，用于渠道名称翻译） */
  channelMap: TChannelMap;
  metric: TMetric;
  height?: number;
  /** 触发 dataZoom 的游戏数量阈值 */
  dataZoomThreshold?: number;
  emptyTitleText: string;
  loading?: boolean;
  /** 按指定渠道的指标值排序游戏 */
  sortByGame?: IGameSortValue;
  /** 图例顺序（游戏 ID 列表），仅控制 Y 轴显示顺序 */
  gameOrder?: string[];
}

const DEFAULT_DATA_ZOOM_THRESHOLD = 8;

/** 使用 Canvas 动态测量 12px Roboto 字体的文本宽度 */
const measureTextWidth = (texts: string[]): number[] => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return [];
  }
  context.font = '12px Roboto';
  return texts.map((text) => context.measureText(text).width || 0);
};

const ChannelShareBarChart = memo(({
  list,
  gameMap,
  channelMap,
  metric,
  height = 380,
  loading,
  dataZoomThreshold = DEFAULT_DATA_ZOOM_THRESHOLD,
  emptyTitleText,
  sortByGame,
  gameOrder,
}: IChannelShareBarChartProps) => {
  const yFormatter = METRIC_METAS[metric]?.yFormatter;
  const valueFormatter =
    yFormatter === 'percent'
      ? (val: number) => (Number.isFinite(val) ? DBO_V.percentageFormat(val * 100) : '-')
      : (val: number) => (Number.isFinite(val) ? DBO_V.reviews(val) : '-');

  const empty = useMemo(() => {
    return (
      !list?.length ||
      list.every(
        (item) =>
          !item.games?.length || item.games.every((g) => !g.value),
      )
    );
  }, [list]);

  const options = useMemo(() => {
    // ── 转置数据：从 list[channel].games[game] → gameDataMap[gameId] = { channelId: value } ──
    const gameDataMap = new Map<string, Map<string, number>>();
    list.forEach((c) => {
      c.games.forEach((g) => {
        if (!gameDataMap.has(g.game_id)) {
          gameDataMap.set(g.game_id, new Map());
        }
        gameDataMap.get(g.game_id)!.set(c.channel_id, g.value || 0);
      });
    });

    // 渠道首次出现顺序
    const channelIdsInOrder = list.map((c) => c.channel_id);

    // Y 轴：游戏 ID 列表（按 gameOrder 或数据中出现顺序）
    const gameIdsFromData = Array.from(gameDataMap.keys());
    const yAxisGameIds = gameOrder?.length
      ? [...gameOrder].filter((id) => gameDataMap.has(id))
      : gameIdsFromData;

    // 排序：按各游戏总值或指定渠道的值排序
    const isTotalSort = !sortByGame?.by || sortByGame.by === '__total__';
    const sortedGameIds = [...yAxisGameIds].sort((a, b) => {
      const getValue = (gameId: string) => {
        const gData = gameDataMap.get(gameId);
        if (!gData) return 0;
        if (isTotalSort) {
          let total = 0;
          gData.forEach((v) => {
            total += v;
          });
          return total;
        }
        return gData.get(sortByGame!.by) || 0;
      };
      const valA = getValue(a);
      const valB = getValue(b);
      return sortByGame?.order === 'asc' ? valA - valB : valB - valA;
    });

    // 渠道堆叠顺序：按第一个游戏中各渠道值从大到小
    const topGame = sortedGameIds[0];
    const firstGameValues = new Map<string, number>();
    if (topGame) {
      const gData = gameDataMap.get(topGame);
      if (gData) {
        gData.forEach((v, cid) => firstGameValues.set(cid, v));
      }
    }
    const stackedChannelIds = [...channelIdsInOrder].sort((a, b) =>
      (firstGameValues.get(b) || 0) - (firstGameValues.get(a) || 0),
    );

    // 图例：渠道列表
    const legendData = stackedChannelIds.slice();

    // 构造 series：每个渠道一条堆叠柱（stack: 'total'），按排序后的游戏顺序填值
    const echartSeries = stackedChannelIds.map((channelId) => {
      const data = sortedGameIds.map((gameId) => {
        const gData = gameDataMap.get(gameId);
        return gData?.get(channelId) || 0;
      });
      const { img } = channelInfo(channelId as ChannelIconType, undefined, true);
      return {
        name: channelId,
        type: 'bar' as const,
        stack: 'total',
        data,
        barMaxWidth: 80,
        label: {
          show: false,
          position: 'right' as const,
          formatter: (params: any) => valueFormatter(params.value),
          fontSize: 12,
        },
        ...(img ? { icon: `image://${img}` } : {}),
      };
    });

    // 游戏图标 rich 配置
    const gameRich = buildGameRich(
      sortedGameIds.map((id) => ({ game_id: id, values: [] })),
      gameMap,
      20,
    );

    // 游戏名称列表，用于测量文本宽度
    const gameNames = sortedGameIds.map((gameId) =>
      gameMap[gameId]?.game_name || gameId,
    );

    const maxLabelWidth =
      Math.max(...(measureTextWidth(gameNames) || [])) || 60;
    const rounded = Math.round(maxLabelWidth / 10) * 10;
    const labelWidth =
      (rounded > maxLabelWidth ? rounded : rounded + 10) + 10;

    const needDataZoom = sortedGameIds.length > dataZoomThreshold;

    // tooltip
    const tooltip: any = {
      enterable: true,
      className: `dbom-ad-comparison-channel-bar-chart-tooltip ${ClassSpecial.CHART_TOOLTIP_SCROLL}`,
      formatter: (params: any) => {
        const gameId = params?.[0]?.axisValue as string;
        return formatChartParamsDate({
          title: gameTooltipLabel(gameId, gameMap),
          params,
          isSort: true,
          textShowFun: (marker: string, seriesName: string) =>
            channelShowElement(marker, seriesName, 'dbom-ad-comparison-channel-bar-chart-tooltip', true),
          valueFormat: valueFormatter,
          dealwithRecords: (records: any, total: number) =>
            records.map((item: any) => {
              if (item.originValue === 0 || yFormatter === 'percent') return item;
              return {
                ...item,
                value: `${item.value}(${DBO_V.percentage(item.originValue, total, 2)})`,
              };
            }),
        });
      },
    };

    const baseOptions: any = {
      tooltip,
      series: echartSeries,
      xAxis: {
        type: 'value',
        boundaryGap: '0%',
        ...(yFormatter === 'percent' ? { max: 1 } : {}),
        axisLabel: {
          formatter: (value: number) =>
            yFormatter === 'percent' ? DBO_EC.percent(value, false) : DBO_EC.reviews(value),
          color: '#838697',
          fontSize: 12,
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'solid' },
        },
        splitLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
      },
      yAxis: [
        {
          type: 'category',
          position: 'left',
          data: sortedGameIds,
          inverse: true,
          axisLabel: {
            interval: 0,
            show: true,
            formatter: (gameId: string) => {
              const key = gameRichKey(gameId);
              const iconKey = gameRich[key] ? key : gameRichKey('default');
              return `{${iconKey}|}`;
            },
            rich: gameRich,
            width: 20,
            align: 'right',
            padding: [0, labelWidth + 6, 0, 0],
            height: 20,
            verticalAlign: 'middle',
          },
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
        },
        {
          type: 'category',
          position: 'left',
          data: sortedGameIds,
          inverse: true,
          axisLabel: {
            interval: 0,
            show: true,
            formatter: (gameId: string) => {
              const name = gameMap[gameId]?.game_name || gameId;
              return `{label|${name}}`;
            },
            rich: {
              label: {
                align: 'left',
              },
            },
            padding: [0, 0, 0, 0],
            overflow: 'break',
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 20,
            width: labelWidth + 4,
          },
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
        },
      ],
      legend: {
        show: true,
        type: 'scroll' as const,
        data: legendData,
        formatter: (channelId: string) => {
          const { name, img } = channelInfo(channelId as ChannelIconType, "default", true);
          const key = img === "default" ? "default" : dealwithChannelChartKey(channelId);
          return `{${key}|} {name|${name}}`;
        },
        textStyle: {
          rich: { 
            name: {
              padding: [0, 0, 0, 4],
              fontSize: 12,
              verticalAlign: 'middle',
            },
          },
        },
      },
      grid: {
        left: 0,
        right: 20,
        top: 10,
        contentLabel: true,
      },
    };

    if (needDataZoom) {
      baseOptions.dataZoom = [
        {
          type: 'inside',
          yAxisIndex: [0, 1],
          filterMode: 'none',
          maxValueSpan: dataZoomThreshold - 1,
          minValueSpan: dataZoomThreshold - 1,
          startValue: 0,
          endValue: dataZoomThreshold - 1,
          moveOnMouseWheel: true,
          zoomOnMouseWheel: false,
        },
        {
          type: 'slider',
          filterMode: 'none',
          show: true,
          yAxisIndex: [0, 1],
          width: 0,
          maxValueSpan: dataZoomThreshold - 1,
          minValueSpan: dataZoomThreshold - 1,
          startValue: 0,
          endValue: 100,
          right: 10,
          showDetail: false,
          showDataShadow: false,
          moveHandleSize: 8,
          moveHandleIcon: 'none',
          zoomLock: true,
          borderColor: 'rgba(0,0,0,0)',
          moveHandleStyle: { color: '#D8DAE4', borderCap: 'square' },
          emphasis: { moveHandleStyle: { color: '#D8DAE4' } },
        },
      ];
    }

    return baseOptions;
  }, [list, dataZoomThreshold, gameMap, metric, sortByGame, gameOrder, channelMap]);

  return (
    <EChartCommon
      options={options}
      optionsProps={{ type: 'bar', excludeOptions: ['axisPointer'], isMarketing: true }}
      height={height}
      emptyTitleText={emptyTitleText}
      empty={empty}
      loading={loading}
      hasEmptyDesc={false}
    />
  );
});

ChannelShareBarChart.displayName = 'ChannelShareBarChart';

export default ChannelShareBarChart;
